import { useCallback, useEffect, useState } from "react";
import { PageHeader } from "../components/PageHeader";
import { Banner, CardGrid, Chip } from "../ui/primitives";
import type { Tone } from "../ui/tone";
import { AuthRequired } from "../components/AuthRequired";
import { ApiHttpError } from "../api/client";
import { AuthorityNotice, GAOSAFE_APPROVAL_REASON, useWritesBlocked } from "../authority";
import {
  getSettlementControlStatus,
  resumeSettlementControl,
  createResumeProposal,
  validateResumeProposalId,
  isDryRunResult,
  type SettlementControlStatus,
  type ResumeDryRunResult,
  type ResumeExecuteResult,
} from "../api/settlementControl";

// H-8 settlement-control operator screen.
//
// Browser calls ONLY the session-gated BFF (/v2/admin/bff/settlement-control/*)
// — never the HMAC routes, never an HMAC header. The execute step is
// `low_config`: the operator supplies an APPROVED multisign proposalId
// (created + approved via the Approvals flow). The browser never bypasses the
// proposal guard and never settles directly.

const DRY_RUN_REASON = "operator reviewing H-8 resume from Admin Web";
const EXECUTE_REASON = "operator confirmed chain safety and resume from Admin Web";

function friendlyError(e: unknown): string {
  if (e instanceof ApiHttpError && (e.status === 401 || e.status === 403)) {
    return "Admin session or approval required.";
  }
  return "Something went wrong. Please try again in a moment.";
}

export function SettlementControl() {
  return (
    <div>
      <PageHeader
        title="System health"
        subtitle="Service state, and the control to resume settlement after a safety pause."
      />
      <AuthRequired>
        <SettlementControlInner />
      </AuthRequired>
    </div>
  );
}

function SettlementControlInner() {
  const [status, setStatus] = useState<SettlementControlStatus | null>(null);
  // Hook — must sit with the other hooks, above the loading/error early returns.
  const writesBlocked = useWritesBlocked();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dryRun, setDryRun] = useState<ResumeDryRunResult | null>(null);
  const [dryRunBusy, setDryRunBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [proposalId, setProposalId] = useState("");
  const [execBusy, setExecBusy] = useState(false);
  const [execResult, setExecResult] = useState<ResumeExecuteResult | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [createdProposalId, setCreatedProposalId] = useState<string | null>(null);
  const [proposalBusy, setProposalBusy] = useState(false);

  const createProposal = useCallback(async () => {
    setProposalBusy(true);
    setActionError(null);
    try {
      // Unscoped resume (matches the unscoped dry-run/execute on this screen).
      const res = await createResumeProposal({ reason: EXECUTE_REASON });
      setCreatedProposalId(res.proposalId);
      setProposalId(res.proposalId); // pre-fill; operator still approves it first
    } catch (e) {
      setActionError(friendlyError(e));
    } finally {
      setProposalBusy(false);
    }
  }, []);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setStatus(await getSettlementControlStatus());
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const startDryRun = useCallback(async () => {
    setDryRunBusy(true);
    setActionError(null);
    setExecResult(null);
    setProposalId("");
    setCreatedProposalId(null);
    try {
      const res = await resumeSettlementControl({ dryRun: true, reason: DRY_RUN_REASON });
      if (isDryRunResult(res)) {
        setDryRun(res);
        setModalOpen(true);
      }
    } catch (e) {
      setActionError(friendlyError(e));
    } finally {
      setDryRunBusy(false);
    }
  }, []);

  const confirmExecute = useCallback(async () => {
    if (!proposalId.trim()) return;
    setExecBusy(true);
    setActionError(null);
    try {
      const res = await resumeSettlementControl({
        dryRun: false,
        reason: EXECUTE_REASON,
        proposalId: proposalId.trim(),
      });
      if (!isDryRunResult(res)) setExecResult(res);
      setModalOpen(false);
      setDryRun(null);
      await loadStatus(); // refresh H-8 status + any cached admin status
    } catch (e) {
      setActionError(friendlyError(e));
    } finally {
      setExecBusy(false);
    }
  }, [proposalId, loadStatus]);

  if (loading) {
    return <p>Loading settlement status…</p>;
  }
  if (error) {
    return (
      <div role="alert" style={{ color: "var(--danger, #b91c1c)" }}>
        <p>{error}</p>
        <button type="button" onClick={() => void loadStatus()}>
          Retry
        </button>
      </div>
    );
  }
  if (!status) return null;

  const tripped = status.breaker.tripped;
  const dryRunSafe =
    dryRun !== null &&
    dryRun.nextAction === "confirm_resume" &&
    dryRun.safeCount > 0 &&
    dryRun.blockedCount === 0;
  const proposalIdError = proposalId.trim().length > 0 ? validateResumeProposalId(proposalId) : null;
  const confirmEnabled = dryRunSafe && validateResumeProposalId(proposalId) === null && !execBusy;

  // ─── Service status (§10) ──────────────────────────────────────────────
  //
  // The brief asks for a card per Gao service — Gao ID, Control Plane, Admin,
  // Browser, GaoKey — each HEALTHY / DEGRADED / DOWN / UNKNOWN.
  //
  // There is no per-service health endpoint. `src/api/` contains no health
  // module, and the only status this console can read is
  // /v2/admin/bff/settlement-control/status, which returns exactly three
  // booleans about payment settlement. Rendering five green cards from one
  // settlement flag would be a fabrication on the screen an operator opens to
  // find out whether something is broken — which is the one screen where a
  // confident wrong answer costs the most.
  //
  // So the cards below are the ones that have real data behind them, and the
  // rest of the fleet is stated as NOT INSTRUMENTED rather than drawn green.
  // UNKNOWN is a first-class state here for exactly that reason.
  const cards: ReadonlyArray<{ name: string; state: string; tone: Tone; detail: string }> = [
    {
      name: "Payment settlement",
      state: tripped ? "PAUSED" : "HEALTHY",
      tone: tripped ? "danger" : "ok",
      detail: tripped
        ? "Circuit breaker tripped — settlement is held"
        : "Circuit breaker clear",
    },
    {
      name: "Settlement processing",
      state: status.settlementEnabled ? "ENABLED" : "DISABLED",
      tone: status.settlementEnabled ? "ok" : "warn",
      detail: "Off-chain settlement pipeline",
    },
    {
      name: "On-chain settlement",
      state: status.onchainSettlementEnabled ? "ENABLED" : "DISABLED",
      tone: status.onchainSettlementEnabled ? "ok" : "warn",
      detail: "Deposit confirmation on chain",
    },
    {
      name: "Gao ID API",
      state: "REACHABLE",
      tone: "ok",
      // Not a health check — but not nothing either: this page's own status
      // request went to that host and came back, which is a real fact and the
      // only reachability signal the console has.
      detail: "This page's request succeeded",
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <CardGrid min={228} gap={12}>
          {cards.map((c) => (
            <div
              key={c.name}
              className="g-card"
              style={{
                padding: "14px 16px",
                display: "flex",
                flexDirection: "column",
                gap: 6,
                borderTop: `2px solid var(--${c.tone})`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ flex: "1 1 auto", fontSize: 13.5, fontWeight: 620 }}>
                  {c.name}
                </span>
                <Chip tone={c.tone} solid size="sm">
                  {c.state}
                </Chip>
              </div>
              <span style={{ fontSize: 12, color: "var(--fg-dim)" }}>{c.detail}</span>
            </div>
          ))}
        </CardGrid>

        <div style={{ marginTop: 12 }}>
          <Banner tone="info" compact>
            Only payment settlement is instrumented. Browser, GaoKey, Meshii and the Control
            Plane have no health endpoint this console can call, so they are not shown as
            healthy — their state here is unknown.
          </Banner>
        </div>
      </div>

      {tripped ? (
        <div
          role="alert"
          data-testid="h8-tripped"
          style={{
            border: "1px solid var(--danger, #fca5a5)",
            background: "var(--danger-bg, #fef2f2)",
            borderRadius: 12,
            padding: 16,
            marginBottom: 16,
          }}
        >
          <strong style={{ color: "var(--danger, #b91c1c)" }}>
            Settlement paused for chain safety
          </strong>
          <p style={{ margin: "8px 0 4px" }}>
            Reason: <code>{status.breaker.reason ?? "unknown"}</code>
          </p>
          {status.breaker.trippedAt && (
            <p style={{ margin: "4px 0", color: "var(--fg-dim)" }}>
              Tripped at: {new Date(status.breaker.trippedAt).toLocaleString()}
            </p>
          )}
          <p style={{ margin: "4px 0", color: "var(--fg-dim)" }}>
            settlementEnabled: {String(status.settlementEnabled)} · onchainSettlementEnabled:{" "}
            {String(status.onchainSettlementEnabled)}
          </p>
          <p style={{ margin: "8px 0", fontSize: 13 }}>
            Payment settlement will resume only for verified canonical-final deposits.
          </p>
          <button
            type="button"
            onClick={writesBlocked ? undefined : () => void startDryRun()}
            disabled={writesBlocked || dryRunBusy}
            title={writesBlocked ? GAOSAFE_APPROVAL_REASON : undefined}
          >
            {dryRunBusy ? "Checking…" : "Confirm safe & continue settlement"}
          </button>
          {writesBlocked && (
            <div style={{ marginTop: 10 }}>
              <AuthorityNotice scope="Resuming settlement is blocked." />
            </div>
          )}
        </div>
      ) : (
        <div
          data-testid="h8-clear"
          style={{ border: "1px solid var(--ok, #86efac)", borderRadius: 12, padding: 16, marginBottom: 16 }}
        >
          <strong style={{ color: "var(--ok, #15803d)" }}>Settlement healthy</strong>
          <p style={{ margin: "8px 0 0", color: "var(--fg-dim)" }}>
            H-8 breaker is clear. settlementEnabled: {String(status.settlementEnabled)} ·
            onchainSettlementEnabled: {String(status.onchainSettlementEnabled)}
          </p>
        </div>
      )}

      {actionError && (
        <p role="alert" style={{ color: "var(--danger, #b91c1c)" }}>
          {actionError}
        </p>
      )}

      {execResult && (
        <div
          data-testid="resume-result"
          style={{ border: "1px solid var(--border)", borderRadius: 12, padding: 16, marginBottom: 16 }}
        >
          <strong>Resume complete</strong>
          <p style={{ margin: "8px 0" }}>
            breakerReset: {String(execResult.breakerReset)} · settled: {execResult.settledCount} ·
            stillProcessing: {execResult.stillProcessingCount} · failed: {execResult.failedCount}
          </p>
          <table>
            <thead>
              <tr>
                <th>intent</th><th>domain</th><th>before→after</th><th>billing</th>
                <th>domain</th><th>mapping</th><th>resolver</th>
              </tr>
            </thead>
            <tbody>
              {execResult.results.map((r) => (
                <tr key={r.intentId}>
                  <td><code>{r.intentId}</code></td>
                  <td>{r.domain}</td>
                  <td>{r.before} → {r.after}</td>
                  <td>{r.billingAfter ?? "—"}</td>
                  <td>{String(r.domainCreated)}</td>
                  <td>{String(r.mappingCreated)}</td>
                  <td>{String(r.resolverOk)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && dryRun && (
        <div
          role="dialog"
          aria-label="Confirm safe & continue settlement"
          data-testid="resume-modal"
          style={{ border: "1px solid var(--border)", borderRadius: 12, padding: 16 }}
        >
          <h3>Review before continuing settlement</h3>
          <p style={{ fontSize: 13 }}>
            This verifies canonical-final deposits, resets H-8 only if safe, then runs official
            settlement. It will not refund, create new payment intents, or manually edit the DB.
          </p>
          <p>
            Affected: {dryRun.affectedCount} · safe: {dryRun.safeCount} · blocked: {dryRun.blockedCount}
          </p>
          <table>
            <thead>
              <tr>
                <th>intent</th><th>domain</th><th>status</th><th>billing</th>
                <th>tx</th><th>safe?</th><th>blockers</th>
              </tr>
            </thead>
            <tbody>
              {dryRun.intents.map((it) => (
                <tr key={it.intentId} data-testid="dryrun-intent">
                  <td><code>{it.intentId}</code></td>
                  <td>{it.domain}</td>
                  <td>{it.status}</td>
                  <td>{it.billingStatus ?? "—"}</td>
                  <td><code>{it.txHash ?? "—"}</code></td>
                  <td>{String(it.safeToResume)}</td>
                  <td>{it.blockers.join(", ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {dryRunSafe ? (
            <div style={{ marginTop: 12 }}>
              <div style={{ marginBottom: 8 }}>
                <button type="button" onClick={createProposal} disabled={proposalBusy}>
                  {proposalBusy ? "Creating…" : "Create resume proposal"}
                </button>
                {createdProposalId && (
                  <p data-testid="created-proposal" style={{ fontSize: 12, color: "var(--fg-dim)", margin: "6px 0" }}>
                    Created proposal <code>{createdProposalId}</code>. Now <strong>approve it in
                    Approvals</strong>, then continue below.
                  </p>
                )}
              </div>
              <label>
                Approved resume proposalId{" "}
                <input
                  type="text"
                  aria-label="proposalId"
                  value={proposalId}
                  onChange={(e) => setProposalId(e.target.value)}
                  placeholder="prop_… (create + approve a resume proposal)"
                />
              </label>
              <p style={{ fontSize: 12, color: "var(--fg-dim)" }}>
                Create and approve a resume proposal, then paste proposalId here.
              </p>
              {proposalIdError && (
                <p role="alert" data-testid="proposalid-error" style={{ fontSize: 12, color: "var(--danger, #b91c1c)" }}>
                  {proposalIdError}
                </p>
              )}
            </div>
          ) : (
            <p
              role="alert"
              data-testid="dryrun-blocked"
              style={{ color: "var(--danger, #b91c1c)" }}
            >
              {dryRun.nextAction === "already_clear"
                ? "Breaker is already clear — nothing to resume."
                : "Cannot continue: some deposits are not yet safe. Resolve the blockers above."}
            </p>
          )}

          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <button type="button" onClick={confirmExecute} disabled={!confirmEnabled}>
              {execBusy ? "Continuing…" : "Confirm safe & continue"}
            </button>
            <button
              type="button"
              onClick={() => {
                setModalOpen(false);
                setDryRun(null);
              }}
              disabled={execBusy}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
